/**
 * Providers for GlobalSearch, one per element type. See GlobalSearch.vue for the provider interface.
 * Add a factory here for each new element type, and open the search with several of them for a general search.
 */

/**
 * The global search: users and workouts of the current organization that the user may open, from one request per query.
 * Selecting a result opens its page.
 *
 * @param http The axios instance
 * @param router The Vue router
 * @param includeUsers Whether the user may see users at all. Runners can't, so they search workouts only.
 */
export function globalSearchProviders({http, router, includeUsers}) {
    // The providers share the response of the latest query
    let lastQuery = null;
    let lastResponse = null;
    const fetch = query => {
        if (query !== lastQuery) {
            lastQuery = query;
            lastResponse = http.get('/api/search', {params: {query}}).then(response => response.data);
        }
        return lastResponse;
    };
    const open = location => router.push(location).catch(() => {
        // Already there
    });

    // field: the response's list
    const usersProvider = (key, title, icon, colorClass, field) => ({
        key,
        title,
        icon,
        colorClass,
        search(query) {
            return fetch(query).then(data => (data[field] || []).map(user => ({
                id: user.uuid,
                title: user.name || user.username,
                subtitle: user.name ? user.username : '',
                username: user.username,
            })));
        },
        select(item) {
            // Like clicking a runner's name on the Runners page. Users and Teams lists the inactive runners too.
            open({name: 'users-and-teams', query: {q: item.username}});
        },
    });

    // In display order: workouts, active runners, inactive runners
    const providers = [{
        key: 'workouts',
        title: 'Workouts',
        icon: 'running',
        colorClass: 'text-workout',
        search(query) {
            return fetch(query).then(data => data.workouts.map(workout => ({
                id: workout.uuid,
                title: workout.name,
            })));
        },
        select(item) {
            open({name: 'workout', params: {uuid: item.id}});
        },
    }];
    if (includeUsers) {
        providers.push(usersProvider('users', 'Runners', 'user', 'text-runner', 'users'));
        // Only Owners and Admins get any: they can activate them again
        providers.push(usersProvider('inactive-users', 'Inactive Runners', 'ban', 'text-runner-inactive', 'inactiveUsers'));
    }
    return providers;
}

/**
 * The pages to go to (OSA-136), matched by every word typed, as the global search does (OSA-101). Selecting one opens it.
 *
 * @param router The Vue router
 * @param pages The pages this user may open, in display order: [{id, title, icon, location}]
 */
export function navigationProvider({router, pages}) {
    return {
        key: 'navigation',
        title: 'Go to',
        search(query) {
            const words = query.toLowerCase().split(/\s+/).filter(word => word);
            return Promise.resolve(pages.filter(page => words.every(word => page.title.toLowerCase().includes(word))));
        },
        select(page) {
            router.push(page.location).catch(() => {
                // Already there
            });
        },
    };
}

/**
 * Users matched by username or name. SuperAdmin only.
 *
 * @param http The axios instance
 * @param select Called with the chosen user: {uuid, username, name}
 * @param excludeUsernames Users not to list, such as the active user
 */
export function usersProvider({http, select, excludeUsernames = []}) {
    return {
        key: 'users',
        title: 'Users',
        icon: 'user',
        search(query) {
            return http.get('/api/sa/users/search', {params: {query}})
                .then(response => response.data
                    .filter(user => !excludeUsernames.includes(user.username))
                    .map(user => ({
                        id: user.uuid,
                        title: user.name || user.username,
                        subtitle: user.name ? user.username : '',
                        user,
                    })));
        },
        select(item) {
            select(item.user);
        },
    };
}
